import React, { useState, useEffect } from 'react';
import { lowStockAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { AlertTriangle, TrendingDown, Package, Loader2, ChevronDown, ChevronUp, Truck, Warehouse } from 'lucide-react';

/**
 * LowStockAlerts — Stage 6C
 * Surfaces depletion predictions as warnings. Can be filtered by source.
 *
 * Props:
 *   sourceType: "vehicle" | "warehouse" | null (all)
 *   sourceId: filter to a specific vehicle/warehouse
 *   compact: show fewer items
 */
export function LowStockAlerts({ sourceType, sourceId, compact = false }) {
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(!compact);

  useEffect(() => {
    const fetch = async () => {
      try {
        const params = { risk_only: true, threshold_days: 7 };
        if (sourceType) params.source_type = sourceType;
        const res = await lowStockAPI.depletionPredictions(params);
        let items = res.data || [];
        if (sourceId) {
          items = items.filter(i => i.source_id === sourceId);
        }
        setPredictions(items);
      } catch { /* non-critical */ }
      setLoading(false);
    };
    fetch();
  }, [sourceType, sourceId]);

  if (loading) return null;
  if (predictions.length === 0) return null;

  const shown = compact && !expanded ? predictions.slice(0, 3) : predictions;

  return (
    <Card className="border-amber-200 bg-amber-50/30 dark:bg-amber-950/10 dark:border-amber-800" data-testid="low-stock-alerts">
      <CardHeader className="pb-2 pt-3 px-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold flex items-center gap-2 text-amber-700 dark:text-amber-400">
            <AlertTriangle className="w-4 h-4" />
            Low Stock Risk ({predictions.length})
          </CardTitle>
          {compact && predictions.length > 3 && (
            <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ChevronUp className="w-3 h-3 mr-1" /> : <ChevronDown className="w-3 h-3 mr-1" />}
              {expanded ? 'Less' : `+${predictions.length - 3} more`}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-3">
        <div className="space-y-1.5">
          {shown.map((p, idx) => (
            <div key={`${p.source_id}-${p.item_id}`} className="flex items-center justify-between text-sm py-1 border-b border-amber-200/50 last:border-0" data-testid={`low-stock-row-${idx}`}>
              <div className="flex items-center gap-2 min-w-0 flex-1">
                {p.source_type === 'vehicle' ? (
                  <Truck className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                ) : (
                  <Warehouse className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                )}
                <span className="truncate">
                  <span className="font-medium">{p.source_name}</span>
                  {' — '}
                  <span className="text-muted-foreground">{p.item_name}</span>
                </span>
              </div>
              <div className="flex items-center gap-3 shrink-0 ml-2">
                <span className="text-xs text-muted-foreground tabular-nums">{p.qty_on_hand} left</span>
                <span className={`text-xs font-semibold tabular-nums ${
                  p.days_until_empty <= 2 ? 'text-red-600' : 'text-amber-600'
                }`} data-testid={`days-left-${idx}`}>
                  ~{p.days_until_empty}d
                </span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * RestockSuggestions — Stage 6D
 * Shows suggested restock quantities for at-risk items.
 */
export function RestockSuggestions({ sourceType, sourceId }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const params = {};
        if (sourceType) params.source_type = sourceType;
        const res = await lowStockAPI.restockSuggestions(params);
        let items = res.data || [];
        if (sourceId) {
          items = items.filter(i => i.source_id === sourceId);
        }
        setSuggestions(items);
      } catch { /* non-critical */ }
      setLoading(false);
    };
    fetch();
  }, [sourceType, sourceId]);

  if (loading) return null;
  if (suggestions.length === 0) return null;

  return (
    <Card className="border-blue-200 bg-blue-50/30 dark:bg-blue-950/10 dark:border-blue-800" data-testid="restock-suggestions">
      <CardHeader className="pb-2 pt-3 px-4">
        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-blue-700 dark:text-blue-400">
          <Package className="w-4 h-4" />
          Restock Suggestions ({suggestions.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-3">
        <div className="space-y-1.5">
          {suggestions.map((s, idx) => (
            <div key={`${s.source_id}-${s.item_id}`} className="flex items-center justify-between text-sm py-1 border-b border-blue-200/50 last:border-0" data-testid={`restock-row-${idx}`}>
              <div className="flex items-center gap-2 min-w-0 flex-1">
                {s.source_type === 'vehicle' ? (
                  <Truck className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                ) : (
                  <Warehouse className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                )}
                <span className="truncate">
                  <span className="font-medium">{s.source_name}</span>
                  {' — '}
                  <span className="text-muted-foreground">{s.item_name}</span>
                </span>
              </div>
              <div className="flex items-center gap-3 shrink-0 ml-2">
                <span className="text-xs text-muted-foreground tabular-nums">{s.qty_on_hand} on hand</span>
                <span className="text-xs font-semibold text-blue-600 tabular-nums" data-testid={`restock-qty-${idx}`}>
                  +{s.suggested_restock_qty}
                </span>
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-2">Based on 14-day usage velocity</p>
      </CardContent>
    </Card>
  );
}
